import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCurrentSession } from '../services/authService';
import { Button, Card, Pill, StatusBadge } from '../ui/primitives';
import {
  IconArrowRight,
  IconCalendar,
  IconCar,
  IconClock,
  IconDollar,
  IconPin,
} from '../ui/icons';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

const DRAFT_STATUSES = new Set(['DRAFT', 'PENDING', 'PENDING_PAYMENT', 'APPROVED', 'CONFIRMED']);
const CONVERTED_STATUSES = new Set(['CONVERTED']);

function bucket(status) {
  const s = (status || '').toUpperCase();
  if (CONVERTED_STATUSES.has(s)) return 'converted';
  if (DRAFT_STATUSES.has(s)) return 'draft';
  return 'other';
}

function formatDate(dt) {
  if (!dt) return null;
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(dt) {
  if (!dt) return null;
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function shortLocation(loc) {
  if (!loc) return '—';
  if (loc.airportCode) return `${loc.city || loc.airportCode} (${loc.airportCode})`;
  return [loc.city, loc.state].filter(Boolean).join(', ') || loc.addressLine1 || '—';
}

const VEHICLE_LABELS = {
  SEDAN: 'Sedan',
  SUV: 'SUV',
  VAN: 'Big Van',
  BIG_VAN: 'Big Van',
  LUXURY: 'Luxury',
  PREMIUM_SUV: 'Premium SUV',
};

function vehicleLabel(quote) {
  const t = quote?.trips && quote.trips[0];
  const code = quote?.vehicleClass || quote?.vehicleType || t?.requestedRideTypeCode || t?.vehicleClass || t?.vehicleType;
  if (!code) return null;
  const up = String(code).toUpperCase();
  return VEHICLE_LABELS[up] || code;
}

function firstTrip(q) {
  return (q?.trips && q.trips[0]) || null;
}

function dedupeByQuoteNumber(quotes) {
  const seen = new Map();
  for (const q of quotes) {
    const key = q.quoteNumber || q.quoteId;
    if (key == null) continue;
    if (!seen.has(key)) seen.set(key, q);
  }
  return Array.from(seen.values());
}

function QuoteCard({ quote, bookingNumber, onBookQuote }) {
  const trip = firstTrip(quote);
  const status = quote.status || 'DRAFT';
  const isDraft = bucket(status) === 'draft';
  const isConverted = bucket(status) === 'converted';

  const from = trip ? shortLocation(trip.pickup) : '—';
  const to = trip ? shortLocation(trip.dropoff) : '—';
  const dt = trip?.pickup?.datetime;
  const date = formatDate(dt);
  const time = formatTime(dt);
  const travelers = trip?.travelers?.numTravelers;
  const bags = trip?.travelers?.numBags;
  const vehicle = vehicleLabel(quote);
  const id = quote.quoteNumber || quote.quoteId;
  const total = quote.totalAmount != null ? Number(quote.totalAmount) : null;
  const currency = quote.currencyCode || 'USD';

  return (
    <Card className="p-5 transition-all hover:border-slate-300 relative">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
          <IconDollar size={20} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="mono text-sm font-semibold text-slate-900">{id}</span>
            <StatusBadge status={status} />
            {isConverted && bookingNumber && (
              <span className="text-xs text-slate-500">
                · Booking{' '}
                <span className="mono font-medium text-slate-700">{bookingNumber}</span>
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-800 font-medium min-w-0">
              <IconPin size={13} className="text-slate-400 shrink-0" />
              <span className="truncate">{from}</span>
            </span>
            <IconArrowRight size={13} className="text-slate-300 shrink-0" />
            <span className="inline-flex items-center gap-1.5 text-slate-800 font-medium min-w-0">
              <IconPin size={13} className="text-slate-400 shrink-0" />
              <span className="truncate">{to}</span>
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
            {date && (
              <span className="inline-flex items-center gap-1">
                <IconCalendar size={12} /> {date}
              </span>
            )}
            {time && (
              <span className="inline-flex items-center gap-1">
                <IconClock size={12} /> {time}
              </span>
            )}
            {travelers != null && (
              <span>
                {travelers} passenger{travelers === 1 ? '' : 's'}
                {bags != null && ` · ${bags} bag${bags === 1 ? '' : 's'}`}
              </span>
            )}
            {vehicle && (
              <span className="inline-flex items-center gap-1">
                <IconCar size={12} /> {vehicle}
              </span>
            )}
          </div>

          {total != null && (
            <div className="mt-3 text-lg font-bold text-indigo-600 mono">
              ${total.toFixed(2)}{' '}
              <span className="text-xs font-medium text-slate-500 mono">{currency}</span>
            </div>
          )}
        </div>

        {isDraft && (
          <div className="shrink-0">
            <Button
              variant="primary"
              size="sm"
              onClick={() => onBookQuote(quote.quoteNumber)}
            >
              Book This Quote
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function MyQuotesPage({ authSession, onBookQuote }) {
  const [quotes, setQuotes] = useState([]);
  const [bookingByQuoteId, setBookingByQuoteId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const userId = authSession?.user?.sub;

  const fetchAll = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      let token = authSession?.idToken;
      try {
        const freshSession = await getCurrentSession();
        token = freshSession.idToken;
      } catch (e) { /* use existing */ }

      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const [quotesRes, bookingsRes] = await Promise.all([
        fetch(`${API_BASE}/api/quotes/by-user/${userId}`, { headers }),
        fetch(`${API_BASE}/api/bookings/by-user/${userId}`, { headers }),
      ]);
      if (!quotesRes.ok) throw new Error(`HTTP ${quotesRes.status}`);
      const quotesData = await quotesRes.json();
      const bookingsData = bookingsRes.ok ? await bookingsRes.json() : [];

      const map = {};
      (Array.isArray(bookingsData) ? bookingsData : []).forEach((b) => {
        const qid = b.quoteId ?? b.quote?.quoteId;
        if (qid != null && b.bookingNumber) map[qid] = b.bookingNumber;
      });
      setBookingByQuoteId(map);
      setQuotes(dedupeByQuoteNumber(Array.isArray(quotesData) ? quotesData : []));
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
      setError('Failed to load your quotes');
    } finally {
      setLoading(false);
    }
  }, [userId, authSession]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const counts = useMemo(() => {
    const c = { all: quotes.length, draft: 0, converted: 0 };
    quotes.forEach((q) => {
      const k = bucket(q.status);
      if (k === 'draft') c.draft += 1;
      if (k === 'converted') c.converted += 1;
    });
    return c;
  }, [quotes]);

  const filtered = useMemo(
    () =>
      quotes.filter((q) => {
        if (filter === 'all') return true;
        return bucket(q.status) === filter;
      }),
    [quotes, filter]
  );

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50">
      <div className="px-6 py-6 max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">My Quotes</h1>
          <p className="text-sm text-slate-500 mt-1">View and manage your quotes.</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Pill active={filter === 'all'} onClick={() => setFilter('all')} count={counts.all}>
            All
          </Pill>
          <Pill active={filter === 'draft'} onClick={() => setFilter('draft')} count={counts.draft}>
            Draft
          </Pill>
          <Pill
            active={filter === 'converted'}
            onClick={() => setFilter('converted')}
            count={counts.converted}
          >
            Converted
          </Pill>
        </div>

        <div>
          {loading ? (
            <Card className="p-10 text-center text-sm text-slate-500">Loading your quotes…</Card>
          ) : error ? (
            <Card className="p-6 text-center">
              <div className="text-sm text-rose-700 mb-3">{error}</div>
              <Button variant="outline" size="sm" onClick={fetchAll}>
                Retry
              </Button>
            </Card>
          ) : filtered.length === 0 ? (
            <Card className="p-10 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
                <IconDollar size={20} />
              </div>
              <div className="text-sm font-semibold text-slate-800">
                {quotes.length === 0 ? 'No quotes yet' : 'Nothing in this view'}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {quotes.length === 0
                  ? 'Your quotes will appear here once you request one.'
                  : 'Switch filters to see other quotes.'}
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {filtered.map((q) => (
                <QuoteCard
                  key={q.quoteNumber || q.quoteId}
                  quote={q}
                  bookingNumber={bookingByQuoteId[q.quoteId]}
                  onBookQuote={onBookQuote}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyQuotesPage;
